'use client';

import {
    faArrowUpRightFromSquare,
    faBookOpen,
    faCircleInfo,
    faExternalLink,
    faPaperPlane,
    faThumbsDown,
    faThumbsUp,
    faTimes,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import { Badge, Button, ScrollArea, Tooltip } from '@ui';
import { StyledInput } from '@ui/Form';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { KeyboardEvent, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClubAdminAction } from '../club-ki-assistant/types';
import {
    getClubAdminActionLabel,
    getVisibleActionPayloadEntries,
    prettifyKey,
    stringifyValue
} from '../club-ki-assistant/utils';
import { EventsCardView, SpacesCardView, StatisticsView, ViewTable } from '../club-ki-assistant/ViewRenderers';
import { renderMarkdown } from '../RenderText';
import { AppSupportCapability } from './constants';
import { AppSupportActionStatus, AppSupportKnowledgeItem, AppSupportMessage, AppSupportView } from './types';
import { resolveClubRoute } from './utils';

// const CAPABILITY_ICONS: Record<AppSupportCapabilityIcon, IconDefinition> = {
//     book: faBookOpen,
//     compass: faCompass,
//     users: faUsers,
//     invoice: faFileInvoice,
//     reminder: faBell,
//     inbox: faInbox,
//     course: faGraduationCap,
//     bookings: faCalendarCheck,
//     spaces: faTableCellsLarge,
//     stats: faChartColumn
// };

// The @ui Button is single-line + fixed-height by default, which clips long
// suggestion text in the narrow chat panel. These overrides let it wrap while
// keeping a comfortable 44px touch target and left-aligned, top-anchored content.
// The @ui Button `sm` size is single-line with a fixed height AND a max-h-9 cap,
// which clips wrapped text at the bottom. These overrides let it grow to fit the
// (2-line clamped) label while keeping a comfortable 44px min touch target.
const SUGGESTION_BUTTON_CLASS =
    '!tw-h-auto !tw-min-h-[44px] !tw-max-h-none !tw-items-start !tw-justify-start !tw-whitespace-normal !tw-py-2.5 !tw-text-left !tw-font-normal !tw-leading-snug';

type AppSupportChatbotViewProps = {
    actionLoadingId: number | null;
    aiEnabled: boolean;
    aiSettingsSaving: boolean;
    approveAction: (messageId: string, action: ClubAdminAction) => void;
    ask: (overrideInput?: string, staticPrompt?: boolean) => Promise<void>;
    canManageAi: boolean;
    capabilities: AppSupportCapability[];
    clubSlug: string;
    disableAi: () => void;
    enableAi: () => void;
    exampleQuestions: string[];
    hasMessages: boolean;
    input: string;
    inputRef: RefObject<HTMLInputElement | null>;
    isClubAdmin: boolean;
    listRef: RefObject<HTMLDivElement | null>;
    loading: boolean;
    messages: AppSupportMessage[];
    onClose?: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    prefillInput: (value: string) => void;
    rejectAction: (messageId: string, action: ClubAdminAction) => void;
    resetConversation: () => void;
    sendFeedback: (messageId: string, usageId: number | null | undefined, rating: 1 | -1) => void;
    setInput: (value: string) => void;
    statusText: string | null;
};

const LIST_ITEM_START = /^(?:\d{1,2}\.|[*-])[ \t]/;

// The model occasionally returns its steps inline ("... . 1. Öffne ... 2. Klicke ...").
// A markdown list item only renders as a list when it starts its own line, so the
// run is broken up again here. The split is anchored on sentence punctuation so
// ordinary German ordinals ("am 5. Januar") stay untouched.
const normalizeStructuredText = (value?: string | null) => {
    if (!value) return '';

    const withLineBreaks = value
        .replace(/([.:;)])[ \t]+(?=\d{1,2}\.[ \t])/g, '$1\n')
        .replace(/([.:;)])[ \t]+(?=[*-][ \t])/g, '$1\n');

    // A list can only interrupt a paragraph when it starts at "1.", so every list
    // gets a blank line ahead of it.
    const lines = withLineBreaks.split('\n');

    return lines
        .map((line, index) => {
            const previous = index > 0 ? lines[index - 1].trim() : '';
            const startsList = LIST_ITEM_START.test(line.trim());

            return startsList && previous !== '' && !LIST_ITEM_START.test(previous) ? `\n${line}` : line;
        })
        .join('\n');
};

/**
 * Waiting state for a turn in flight.
 *
 * A turn is two Bedrock calls plus retrieval and nothing streams back, so the
 * panel would otherwise sit still for several seconds. Same treatment as the
 * WYSIWYG chat's thinking state — bouncing dots, a rotating line and a shimmer
 * track — so the two chats wait the same way.
 */
const THINKING_STEP_MS = 2600;

/** After this long the wait is no longer typical and gets said out loud. */
const THINKING_LONG_WAIT_MS = 20000;

const ThinkingBubble = ({ statusText }: { statusText: string | null }) => {
    const { t } = useTranslation('ai');
    const reduceMotion = useReducedMotion();
    const [step, setStep] = useState(0);
    const [longWait, setLongWait] = useState(false);

    const texts = t('appSupportChat.loading.texts', { returnObjects: true });
    const lines = Array.isArray(texts) ? (texts as string[]) : [];

    // Advances through the lines instead of cycling them: a turn takes long
    // enough that a loop would wrap back to the first line and read as if the
    // assistant had started over. Holding on the last line says "still the same
    // request" — the same reason AnalysisProgress steps rather than switches.
    useEffect(() => {
        if (lines.length < 2) return;

        const timer = setInterval(
            () => setStep((current) => (current + 1 < lines.length ? current + 1 : current)),
            THINKING_STEP_MS
        );

        return () => clearInterval(timer);
    }, [lines.length]);

    useEffect(() => {
        const timer = setTimeout(() => setLongWait(true), THINKING_LONG_WAIT_MS);

        return () => clearTimeout(timer);
    }, []);

    // The retry notice wins over everything: the user is already waiting longer
    // and needs the real reason, not a playful line.
    const label = statusText ?? (longWait ? t('appSupportChat.loading.longer') : lines[step]);

    return (
        <div className="tw-flex tw-justify-start">
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="tw-min-w-0 tw-max-w-full tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-4 md:tw-max-w-3xl"
            >
                <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-accents-7">
                    <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1" aria-hidden="true">
                        {[0, 1, 2].map((index) => (
                            <motion.span
                                key={index}
                                className="tw-inline-block tw-size-1.5 tw-rounded-full tw-bg-accents-6"
                                initial={{ y: 0, opacity: 0.6 }}
                                animate={reduceMotion ? { opacity: 0.6 } : { y: [0, -4, 0], opacity: [0.6, 1, 0.6] }}
                                transition={{
                                    duration: 0.7,
                                    repeat: Infinity,
                                    repeatType: 'loop',
                                    ease: 'easeInOut',
                                    delay: index * 0.12
                                }}
                            />
                        ))}
                    </div>

                    {/* The changing line is what a screen reader needs; the dots and
                        the bar are decoration and stay hidden from it. */}
                    <div role="status" aria-live="polite" className="tw-min-w-0">
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                                key={label}
                                className="tw-block tw-min-w-0"
                                initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                                transition={{ duration: 0.22, ease: 'easeOut' }}
                            >
                                {label}
                            </motion.span>
                        </AnimatePresence>
                    </div>
                </div>

                {/* Deliberately indeterminate. The handler has no progress to report,
                    so a bar that filled up would be inventing one. */}
                <div
                    className="tw-mt-2 tw-h-1 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-accents-1"
                    aria-hidden="true"
                >
                    {!reduceMotion && (
                        <motion.div
                            className="tw-h-full tw-w-1/2 tw-rounded-full tw-bg-accents-5"
                            initial={{ x: '-100%' }}
                            animate={{ x: '200%' }}
                            transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                        />
                    )}
                </div>
            </motion.div>
        </div>
    );
};

const KnowledgeCard = ({ clubSlug, item }: { clubSlug: string; item: AppSupportKnowledgeItem }) => {
    const routes = (item.routen || []).filter((route) => Boolean(route?.route));
    return (
        <div className="tw-min-w-0 tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-3">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accents-6">
                        {item.modul || 'Wissen'}
                    </div>
                    <div className="tw-mt-1 tw-break-words tw-text-sm tw-font-semibold tw-text-accents-9">
                        {item.title || 'Eintrag'}
                    </div>
                </div>
                <FontAwesomeIcon icon={faBookOpen} className="tw-text-accents-5" />
            </div>
            {item.contents && (
                <div className="tw-mt-2 tw-min-w-0 tw-break-words tw-text-sm tw-leading-6 tw-text-accents-8">
                    {renderMarkdown(normalizeStructuredText(item.contents), { emphasizeStrong: false })}
                </div>
            )}
            {routes.length > 0 && (
                <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
                    {routes.map((route) => {
                        const href = resolveClubRoute(route.route, clubSlug);
                        return (
                            <Button.Link
                                key={`${route.route}-${route.routeTitle || 'route'}`}
                                href={href}
                                size="xs"
                                flat
                                color="secondary"
                                className="!tw-rounded-full !tw-border !tw-border-solid !tw-border-accents-2 !tw-bg-white !tw-px-3"
                            >
                                <FontAwesomeIcon icon={faExternalLink} />
                                {route.routeTitle || href}
                            </Button.Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const DataViewCard = ({ clubSlug, view }: { clubSlug: string; view: AppSupportView }) => {
    if (view.type === 'knowledge') return null;

    if (view.type === 'statistics') return <StatisticsView items={view.items} />;
    if (view.type === 'spaces') return <SpacesCardView items={view.items} clubSlug={clubSlug} />;
    if (view.type === 'events') return <EventsCardView items={view.items} clubSlug={clubSlug} />;

    return <ViewTable type={view.type} items={view.items} />;
};

const ActionStatusBadge = ({ status }: { status?: AppSupportActionStatus }) => {
    if (status === 'approved')
        return (
            <Badge flat color="success">
                Genehmigt
            </Badge>
        );
    if (status === 'rejected')
        return (
            <Badge flat color="error">
                Abgelehnt
            </Badge>
        );
    return (
        <Badge flat color="warning">
            Ausstehend
        </Badge>
    );
};

/**
 * Consent prompt shown when the club has not enabled the AI tools. A super admin
 * (Vereinsleitung) gets an activate button; everyone else sees an info hint.
 */
// const PrivacyBanner = ({
//     canManage,
//     onEnable,
//     saving
// }: {
//     canManage: boolean;
//     onEnable: () => void;
//     saving: boolean;
// }) => (
//     <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0 tw-p-4">
//         <div className="tw-flex tw-items-start tw-gap-3">
//             <span className="tw-mt-0.5 tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-success/10 tw-text-success">
//                 <FontAwesomeIcon icon={faShieldHalved} />
//             </span>
//             <div className="tw-min-w-0">
//                 <div className="tw-text-sm tw-font-semibold tw-text-accents-9">{APP_SUPPORT_PRIVACY_NOTICE.title}</div>
//                 <div className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-accents-7">
//                     {APP_SUPPORT_PRIVACY_NOTICE.body}
//                 </div>
//                 {canManage ? (
//                     <>
//                         <div className="tw-mt-3">
//                             <Button
//                                 size="sm"
//                                 color="success"
//                                 className="!tw-rounded-lg"
//                                 isLoading={saving}
//                                 onPress={onEnable}
//                             >
//                                 {APP_SUPPORT_PRIVACY_NOTICE.enable}
//                             </Button>
//                         </div>
//                         <div className="tw-mt-2 tw-text-xs tw-text-accents-6">
//                             {APP_SUPPORT_PRIVACY_NOTICE.managerHint}
//                         </div>
//                     </>
//                 ) : (
//                     <div className="tw-mt-2 tw-text-xs tw-text-accents-6">{APP_SUPPORT_PRIVACY_NOTICE.memberHint}</div>
//                 )}
//             </div>
//         </div>
//     </div>
// );

/** Compact "active for the club" status + off switch — only shown to super admins. */
// const PrivacyStatusRow = ({ onDisable, saving }: { onDisable: () => void; saving: boolean }) => (
//     <div className="tw-mx-3 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-accents-6">
//         <span className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-success" />
//         <span className="tw-min-w-0 tw-flex-1">{APP_SUPPORT_PRIVACY_NOTICE.activeLabel}</span>
//         <Button size="xs" flat color="secondary" isLoading={saving} onPress={onDisable}>
//             {APP_SUPPORT_PRIVACY_NOTICE.disable}
//         </Button>
//     </div>
// );

// const CapabilityList = ({
//     capabilities,
//     onPick,
//     aiEnabled
// }: {
//     capabilities: AppSupportCapability[];
//     onPick: (prompt: string) => void;
//     aiEnabled: boolean;
// }) => (
//     <div className="tw-grid tw-gap-2">
//         {capabilities.map((capability) => {
//             // Admin capabilities need the club-level consent; until then they are
//             // shown (so the value is visible) but not usable.
//             const locked = Boolean(capability.admin) && !aiEnabled;
//             return (
//                 <Button
//                     key={capability.title}
//                     flat
//                     block
//                     color="secondary"
//                     disabled={locked}
//                     icon={<FontAwesomeIcon icon={locked ? faLock : CAPABILITY_ICONS[capability.icon]} />}
//                     onPress={() => onPick(capability.prompt)}
//                     className={SUGGESTION_BUTTON_CLASS}
//                 >
//                     <span className="tw-line-clamp-2 tw-min-w-0">{capability.title}</span>
//                 </Button>
//             );
//         })}
//     </div>
// );

const ActionDetailsModalContent = ({ action }: { action: ClubAdminAction }) => {
    const { t } = useTranslation('ai');
    const payloadEntries = getVisibleActionPayloadEntries(action.payload).filter(
        ([key]) => !['view_type', 'items'].includes(key)
    );

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-text-sm tw-font-medium tw-text-accents-9">
                {getClubAdminActionLabel(action.type, t)}{' '}
                {t('appSupportChat.caseNumberSuffix', { id: action.action_id })}
            </div>
            {action.confirmation_text && (
                <div className="tw-text-sm tw-leading-6 tw-text-accents-8">
                    {renderMarkdown(action.confirmation_text)}
                </div>
            )}
            {payloadEntries.length > 0 ? (
                <div className="tw-divide-y tw-divide-accents-2 tw-rounded-xl tw-bg-accents-1 tw-px-4">
                    {payloadEntries.map(([key, value]) => (
                        <div key={key} className="tw-grid tw-grid-cols-[9rem_1fr] tw-gap-3 tw-py-2">
                            <div className="tw-text-sm tw-font-medium tw-text-accents-7">{prettifyKey(key, t)}</div>
                            <div className="tw-min-w-0 tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-text-accents-9">
                                {stringifyValue(value, t)}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="tw-text-sm tw-text-accents-6">{t('appSupportChat.noDetails')}</div>
            )}
        </div>
    );
};

const ApprovalCard = ({
    action,
    actionLoadingId,
    clubSlug,
    onApprove,
    onReject,
    status
}: {
    action: ClubAdminAction;
    actionLoadingId: number | null;
    clubSlug: string;
    onApprove: () => void;
    onReject: () => void;
    status?: AppSupportActionStatus;
}) => {
    const { t } = useTranslation('ai');
    const { addModal } = useModalManager();
    const payloadEntries = getVisibleActionPayloadEntries(action.payload).filter(
        ([key]) => !['view_type', 'items'].includes(key)
    );
    const previewEntries = payloadEntries.slice(0, 2);
    const isPending = status === undefined || status === 'pending';
    const isBusy = actionLoadingId !== null;

    const openDetails = () =>
        addModal({
            title: `${getClubAdminActionLabel(action.type, t)} ${t('appSupportChat.caseNumberSuffix', { id: action.action_id })}`,
            content: () => <ActionDetailsModalContent action={action} />,
            show: true,
            size: 60
        });

    return (
        <div className="tw-space-y-3 tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <div>
                    <div className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-accents-6">
                        {t('appSupportChat.approvalRequired')}
                    </div>
                    <div className="tw-mt-1 tw-text-sm tw-font-medium tw-text-accents-9">
                        {getClubAdminActionLabel(action.type, t)}{' '}
                        {t('appSupportChat.caseNumberSuffix', { id: action.action_id })}
                    </div>
                </div>
                <ActionStatusBadge status={status} />
            </div>
            {action.confirmation_text && (
                <div className="tw-text-sm tw-leading-6 tw-text-accents-8">
                    {renderMarkdown(action.confirmation_text)}
                </div>
            )}
            {previewEntries.length > 0 && (
                <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                    {previewEntries.map(([key, value]) => (
                        <div
                            key={key}
                            className="tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-3"
                        >
                            <div className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-accents-6">
                                {prettifyKey(key, t)}
                            </div>
                            <div className="tw-mt-1 tw-truncate tw-text-sm tw-text-accents-8">
                                {stringifyValue(value, t)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="tw-flex tw-flex-wrap tw-gap-2">
                {isPending && (
                    <>
                        <Button
                            size="sm"
                            color="success"
                            className="!tw-rounded-lg"
                            isLoading={actionLoadingId === action.action_id}
                            disabled={isBusy || action.action_id === null}
                            onPress={onApprove}
                        >
                            {t('appSupportChat.approve')}
                        </Button>
                        <Button
                            size="sm"
                            color="error"
                            flat
                            className="!tw-rounded-lg"
                            disabled={isBusy || action.action_id === null}
                            onPress={onReject}
                        >
                            {t('appSupportChat.reject')}
                        </Button>
                    </>
                )}
                <Button size="sm" color="secondary" flat className="!tw-rounded-lg" onPress={openDetails}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    {t('appSupportChat.details')}
                </Button>
                <Button.Link
                    href={`/club/${clubSlug}/ai/ai-approvals`}
                    size="sm"
                    color="secondary"
                    flat
                    className="!tw-rounded-lg"
                >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    {t('appSupportChat.goToApproval')}
                </Button.Link>
            </div>
        </div>
    );
};

const FeedbackBar = ({ message, onFeedback }: { message: AppSupportMessage; onFeedback: (rating: 1 | -1) => void }) => (
    <div className="tw-mt-3 tw-flex tw-items-center tw-gap-1">
        <span className="tw-mr-1 tw-text-xs tw-text-accents-6">War diese Antwort hilfreich?</span>
        <Button
            size="xs"
            flat
            color={message.feedback === 'up' ? 'success' : 'secondary'}
            onPress={() => onFeedback(1)}
            className="!tw-min-w-0 !tw-rounded-full !tw-p-1.5"
            aria-label="Hilfreich"
        >
            <FontAwesomeIcon icon={faThumbsUp} />
        </Button>
        <Button
            size="xs"
            flat
            color={message.feedback === 'down' ? 'error' : 'secondary'}
            onPress={() => onFeedback(-1)}
            className="!tw-min-w-0 !tw-rounded-full !tw-p-1.5"
            aria-label="Nicht hilfreich"
        >
            <FontAwesomeIcon icon={faThumbsDown} />
        </Button>
    </div>
);

const MessageBubble = ({
    actionLoadingId,
    approveAction,
    clubSlug,
    message,
    onFeedback,
    rejectAction
}: {
    actionLoadingId: number | null;
    approveAction: (messageId: string, action: ClubAdminAction) => void;
    clubSlug: string;
    message: AppSupportMessage;
    onFeedback?: (messageId: string, usageId: number | null | undefined, rating: 1 | -1) => void;
    rejectAction: (messageId: string, action: ClubAdminAction) => void;
}) => {
    const isUser = message.role === 'user';
    const suggestedHref =
        message.suggestedRoute && clubSlug ? resolveClubRoute(message.suggestedRoute, clubSlug) : null;
    // message.view is the pre-merge shape kept for messages persisted in localStorage.
    const views = message.views ?? (message.view ? [message.view] : []);
    const knowledgeItems = views.flatMap((view) => (view.type === 'knowledge' ? view.items || [] : []));
    const dataViews = views.filter((view) => view.type !== 'knowledge' && (view.items || []).length > 0);
    const actions = message.actions || [];

    return (
        <div className={`tw-flex tw-min-w-0 ${isUser ? 'tw-justify-end' : 'tw-justify-start'}`}>
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`tw-min-w-0 tw-max-w-full tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-p-4 md:tw-max-w-3xl ${
                    isUser
                        ? 'tw-border-success/20 tw-bg-success tw-text-white'
                        : 'tw-border-accents-2 tw-bg-white tw-text-accents-9'
                }`}
            >
                {message.text && (
                    <div className="tw-min-w-0 tw-break-words tw-text-sm tw-leading-6">
                        {isUser ? message.text : renderMarkdown(normalizeStructuredText(message.text), { emphasizeStrong: false })}
                    </div>
                )}

                {knowledgeItems.length > 0 && (
                    <div className="tw-mt-4 tw-space-y-3">
                        {knowledgeItems.map((item, index) => (
                            <KnowledgeCard
                                key={`${item.title || item.modul || 'knowledge'}-${index}`}
                                clubSlug={clubSlug}
                                item={item}
                            />
                        ))}
                    </div>
                )}

                {dataViews.length > 0 && (
                    <div className="tw-mt-4 tw-space-y-3">
                        {dataViews.map((view, index) => (
                            <DataViewCard key={`${view.type}-${index}`} clubSlug={clubSlug} view={view} />
                        ))}
                    </div>
                )}

                {actions.length > 0 && (
                    <div className="tw-mt-4 tw-space-y-3">
                        {actions.map((action, index) => (
                            <ApprovalCard
                                key={`${action.action_id ?? index}`}
                                action={action}
                                actionLoadingId={actionLoadingId}
                                clubSlug={clubSlug}
                                onApprove={() => void approveAction(message.id, action)}
                                onReject={() => void rejectAction(message.id, action)}
                                status={
                                    action.action_id !== null ? message.actionStatus?.[action.action_id] : undefined
                                }
                            />
                        ))}
                    </div>
                )}

                {suggestedHref && message.routeTitle && (
                    <div className="tw-mt-4">
                        <Button.Link
                            color="secondary"
                            href={suggestedHref}
                            className="tw-inline-flex tw-max-w-full tw-flex-wrap tw-items-center tw-gap-2"
                        >
                            <FontAwesomeIcon icon={faExternalLink} />
                            {message.routeTitle}
                        </Button.Link>
                    </div>
                )}

                {!isUser && onFeedback && (
                    <FeedbackBar
                        message={message}
                        onFeedback={(rating) => onFeedback(message.id, message.usageId, rating)}
                    />
                )}
            </motion.div>
        </div>
    );
};

export const AppSupportChatbotView = ({
    actionLoadingId,
    aiEnabled,
    // aiSettingsSaving,
    approveAction,
    ask,
    canManageAi,
    // capabilities,
    clubSlug,
    // disableAi,
    // enableAi,
    exampleQuestions,
    hasMessages,
    input,
    inputRef,
    isClubAdmin,
    listRef,
    loading,
    messages,
    onClose,
    onKeyDown,
    // prefillInput,
    rejectAction,
    resetConversation,
    sendFeedback,
    setInput,
    statusText
}: AppSupportChatbotViewProps) => {
    // const [showCapabilities, setShowCapabilities] = useState(false);

    // const pickCapability = (prompt: string) => {
    //     prefillInput(prompt);
    //     setShowCapabilities(false);
    // };

    // Consent (admin tools) UI states — the club setting, not a per-user choice.
    const showConsentBanner = false; // isClubAdmin && !aiEnabled; // not enabled for the club yet
    const showConsentControl = isClubAdmin && aiEnabled && canManageAi; // super admin can switch off
    // const hasAdminCapabilities = capabilities.some((c) => c.admin);

    return (
        <div className="tw-ml-2 tw-flex tw-h-full tw-min-w-0 tw-flex-col tw-gap-3">
            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                <div className="tw-my-1 tw-flex tw-gap-2">
                    {/* <Tooltip tooltip="Was kann der Assistent?">
                        <Button
                            size="xs"
                            color={showCapabilities ? 'secondary' : 'light'}
                            aria-label="Funktionen anzeigen"
                            aria-pressed={showCapabilities}
                            onPress={() => setShowCapabilities((open) => !open)}
                        >
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                        </Button>
                    </Tooltip> */}
                    <Tooltip tooltip="Chat verlauf löschen">
                        <Button size="xs" onPress={resetConversation} disabled={loading || !hasMessages} color="error">
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </Tooltip>
                    {onClose && (
                        <Tooltip tooltip="Chat schließen">
                            <Button color="light" size="xs" onPress={onClose}>
                                <FontAwesomeIcon icon={faTimes} />
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </div>
            <div className="tw-relative tw-min-h-[420px] tw-flex-1 tw-overflow-hidden tw-rounded-2xl tw-bg-white">
                <ScrollArea className="tw-h-full" gutterStable={false}>
                    <div ref={listRef} className="tw-flex tw-h-full tw-min-w-0 tw-flex-col tw-gap-3">
                        {/* {showCapabilities && (
                            <div className="tw-space-y-4 tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-4">
                                <div>
                                    <div className="tw-text-base tw-font-semibold tw-text-accents-9">
                                        Was der Assistent für dich erledigen kann
                                    </div>
                                    <div className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-accents-7">
                                        Tippe eine Funktion an, um eine passende Frage zu übernehmen.
                                    </div>
                                </div>
                                <CapabilityList
                                    capabilities={capabilities}
                                    onPick={pickCapability}
                                    aiEnabled={aiEnabled}
                                />
                                {hasAdminCapabilities && (
                                    <div className="tw-text-xs tw-leading-5 tw-text-accents-6">
                                        {APP_SUPPORT_CAPABILITY_APPROVAL_HINT}
                                    </div>
                                )}
                            </div>
                        )} */}
                        {!hasMessages ? (
                            <div className="tw-space-y-5">
                                {/* {showConsentBanner && (
                                    <PrivacyBanner
                                        canManage={canManageAi}
                                        onEnable={enableAi}
                                        saving={aiSettingsSaving}
                                    />
                                )}
                                {showConsentControl && (
                                    <PrivacyStatusRow onDisable={disableAi} saving={aiSettingsSaving} />
                                )} */}
                                <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-3">
                                    <div className="tw-text-base tw-font-semibold tw-text-accents-9">
                                        Stelle Fragen zu Buchungen, Einstellungen oder Abläufen
                                    </div>
                                    <div className="tw-mt-1 tw-text-sm tw-text-accents-7">
                                        Der Assistent hilft dir, die gesuchten Informationen schnell zu finden und zeigt
                                        dir, wo du z.B. Einstellungen vornehmen kannst.
                                    </div>
                                </div>
                                <div className="tw-grid tw-gap-2">
                                    {exampleQuestions.map((question) => (
                                        <Button
                                            key={question}
                                            flat
                                            color="secondary"
                                            block
                                            onPress={() => void ask(question, true)}
                                            className={SUGGESTION_BUTTON_CLASS}
                                        >
                                            <span className="tw-line-clamp-2 tw-min-w-0">{question}</span>
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            messages.map((message) => (
                                <MessageBubble
                                    key={message.id}
                                    actionLoadingId={actionLoadingId}
                                    approveAction={approveAction}
                                    clubSlug={clubSlug}
                                    message={message}
                                    onFeedback={sendFeedback}
                                    rejectAction={rejectAction}
                                />
                            ))
                        )}

                        {loading && <ThinkingBubble statusText={statusText} />}
                    </div>
                </ScrollArea>
            </div>
            <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                <StyledInput
                    ref={inputRef}
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={onKeyDown}
                    name="app-support-chatbot"
                    fullWidth
                    bordered
                    size="md"
                    placeholder="Stelle eine Frage zu Sportision..."
                    disabled={loading}
                    className="tw-min-w-0"
                    containerClassName="!tw-rounded-xl !tw-border-accents-2 !tw-bg-accents-0"
                />
                <Button
                    size="md"
                    color="success"
                    disabled={!input.trim() || loading}
                    onPress={() => void ask()}
                    className="!tw-h-10 !tw-min-w-10 !tw-rounded-xl !tw-px-3"
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </Button>
            </div>
        </div>
    );
};
